import React, { useLayoutEffect } from 'react';
import { styled } from '@compiled/react';
import { lazy } from 'react-loosely-lazy';
import { useQueryLoader, type PreloadedQuery } from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import Placeholder from '@atlassian/jira-placeholder';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type { ui_softwareEmptyStateExperienceDeployments_EmptyStateTutorial$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareEmptyStateExperienceDeployments_EmptyStateTutorial.graphql';
import QUERY, {
	type uiDeploymentsEmptyStateExperienceQuery,
} from '@atlassian/jira-relay/src/__generated__/uiDeploymentsEmptyStateExperienceQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { createProjectAri } from './common/utils/ari';
import {
	useViewDeploymentsEmptyStateTutorialExperienceFail,
	ViewDeploymentsEmptyStateTutorialExperienceStart,
} from './services/experience-tracker';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const EmptyStateTutorial = lazy(() =>
	import(
		/* webpackChunkName: "async-software-deployments-empty-state-tutorial" */ './ui/index'
	).then((mod) => mod.EmptyStateTutorial),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const EmptyStateTutorialQueryLoader = lazy(() =>
	import(
		/* webpackChunkName: "async-software-deployments-empty-state-tutorial-query-loader" */ './ui/index'
	).then((mod) => mod.EmptyStateTutorialQueryLoader),
);

/**
 * Async loaded version of the empty state tutorial component. Use this if you
 * wish to compose the component into a larger query.
 *
 * This component omits its own error boundary to ensure that errors are propogated to
 * the parent empty state page for SLI/SLO tracking
 *
 * If you wish for both query loading and component rendering to occur simultaneously,
 * refer to {@link EmptyStateTutorialEntryPoint}
 */
export const AsyncEmptyStateTutorial = ({
	emptyStateTutorial,
}: {
	emptyStateTutorial: ui_softwareEmptyStateExperienceDeployments_EmptyStateTutorial$key;
}) => (
	<Placeholder
		name="empty-state-tutorial"
		fallback={
			<Loading>
				<Spinner size="large" />
			</Loading>
		}
	>
		<EmptyStateTutorial fragmentReference={emptyStateTutorial} origin="deployments-empty-state" />
	</Placeholder>
);

/**
 * Entry point for the empty state tutorial component. Use this if you require
 * both the query loading and component rendering to occur.
 *
 * If you wish to compose this into a larger query, refer to {@link AsyncEmptyStateTutorial}
 */
export const EmptyStateTutorialEntryPoint = ({
	isOpen,
	queryReference: initialQueryReference,
	onClose,
	onInstallBitbucket,
	origin = 'deployments-empty-state',
}: {
	isOpen: boolean;
	queryReference?: PreloadedQuery<uiDeploymentsEmptyStateExperienceQuery>;
	onClose?: () => void;
	onInstallBitbucket?: () => void;
	origin?: 'deployments-empty-state' | 'deployments-timeline';
}) => {
	const [queryReference, loadQuery] = useQueryLoader(QUERY, initialQueryReference);
	const { data: projectContext } = useProjectContext();
	const cloudId = useCloudId();
	const onViewExperienceFail = useViewDeploymentsEmptyStateTutorialExperienceFail();

	useLayoutEffect(() => {
		if (projectContext?.projectId != null && isOpen) {
			loadQuery(
				{
					cloudId,
					projectAri: createProjectAri(cloudId, projectContext.projectId) ?? '',
				},
				{ fetchPolicy: 'store-and-network' },
			);
		}
	}, [isOpen, loadQuery, cloudId, projectContext]);

	return projectContext != null && isOpen ? (
		<>
			<ViewDeploymentsEmptyStateTutorialExperienceStart
				experienceId={String(projectContext.projectId)}
			/>
			<JSErrorBoundary
				fallback="flag"
				id="emptyStateTutorialEntryPoint"
				packageName="jiraSoftwareDeploymentsEmptyStateTutorial"
				teamName="fusion-solaris"
				onError={(location, error) => {
					onViewExperienceFail(location, error);
					if (onClose) {
						onClose();
					}
				}}
			>
				<Placeholder
					name="empty-state-tutorial-query-loader"
					fallback={
						<Loading>
							<Spinner size="large" />
						</Loading>
					}
				>
					{queryReference != null ? (
						<EmptyStateTutorialQueryLoader
							queryReference={queryReference}
							onInstallBitbucket={onInstallBitbucket}
							origin={origin}
						/>
					) : null}
				</Placeholder>
			</JSErrorBoundary>
		</>
	) : null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Loading = styled.div({
	display: 'grid',
	placeItems: 'center',
	padding: `${token('space.1000', '80px')} 0px`,
});
